import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SbDropdown, SbSpin, SbTabs, SbNotification, ArrowLeftOutlined, CaretDownOutlined } from "uiCore";
import useAuthStore from "Store/useAuthStore";
import usePAICStore from "Store/usePAICStore";
import { getPAICRoleImpact } from "Api/apiService";
import analytics from "Utils/analytics";
import { handlePAICErrorCode, PAIC_ERROR_MESSAGES } from "constants/peoplePlusAI.constants";
import { PAIC_ROUTES } from "constants/paicRoutes.constants";
import { Typography } from "antd";

import {
  GROUP_DETAIL_TABS,
  PEOPLE_PLUS_AI_MENU_ITEMS,
} from "constants/groupAgents.constants";

import {
  PageContainer,
  HeaderSection,
  HeaderTopRow,
  BackButton,
  TabsAndControlsBlock,
  TabsRow,
  TabLabelWithCaret,
  ContentSection,
  RoleImpactDetailsContainer,
  TopSection,
  SectionTitle,
  SectionSubTitle,
  OverallPotentialCard,
  OverallPotentialRow,
  OverallPotentialLabel,
  ProgressBarContainer,
  ProgressBarCurrent,
  ProgressBarAI,
  TasksTableWrapper,
  TableElement,
  TableCol,
  TableHeadCell,
  TableBodyCell,
  SortHeaderButton,
  SortHeaderLabel,
  SortIcon,
  TaskDescription,
  CollaborationPotentialCell,
  ExpandButton,
  ChevronIcon,
  ExpandedRow,
  ExpandedContent,
  ExpandedContentInner,
  ExpandedSectionTitle,
  WorkflowCard,
  WorkflowCardHeader,
  WorkflowCardContent,
  EmptyExpandState,
  ComingSoonText,
  LoadingContainer,
  SpacerCell,
} from "./SbPACRoleImpactDetailsV2.styles";
const { Title, Text } = Typography;

const SbPACRoleImpactDetailsV2 = () => {
  const navigate = useNavigate();

  const email = useAuthStore((s) => s.email);
  const selectionId = usePAICStore((s) => s.selectionId);
  const roleSBUniqueId = usePAICStore((s) => s.roleSBUniqueId);
  const loading = usePAICStore((s) => s.loading);
  const setLoading = usePAICStore((s) => s.setLoading);

  const [activeTab, setActiveTab] = useState("people-plus-ai");
  const [roleImpactData, setRoleImpactData] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortColumn, setSortColumn] = useState("weighted_ai");
  const [sortOrder, setSortOrder] = useState("desc");

  const formatPercentage = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return "0%";
    return `${Math.round(num)}%`;
  };

  const fetchRoleImpact = async () => {
    if (!selectionId || !roleSBUniqueId) {
      return;
    }

    setLoading(true);
    try {
      const response = await getPAICRoleImpact({
        selection_id: selectionId,
        sb_unique_id: roleSBUniqueId,
      });
      if (response && response.length > 0) {
        setRoleImpactData(response[0]);
      }
    } catch (error) {
      if (!handlePAICErrorCode(error, SbNotification.error)) {
        SbNotification.error(PAIC_ERROR_MESSAGES.LOAD_ROLE_IMPACT_FAILED);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoleImpact();
  }, [selectionId, roleSBUniqueId]);

  useEffect(() => {
    if (roleImpactData) {
      analytics.trackPAICRoleImpactDetailsViewed({
        from: "pac_role_impact_details",
        email,
        roleId: roleSBUniqueId,
        roleName: roleImpactData.job_title,
        description: `Viewed detailed role impact for: ${roleImpactData.job_title}`,
      });
    }
  }, [roleImpactData, email, roleSBUniqueId]);

  const handleOnBackClick = () => {
    analytics.trackPAICCloseRoleImpactDetails({
      from: "pac_role_impact_details",
      email,
      description: "Closed role impact details view",
    });

    navigate(PAIC_ROUTES.ROLE_IMPACT, {
      state: { fromParent: true },
    });
  };

  const handleTabClick = (tabKey) => {
    setActiveTab(tabKey);
  };

  const handlePeopleAIMenuClick = ({ key }) => {
    setActiveTab("people-plus-ai");

    if (key === "for-role") {
      return;
    }
  };

  const handleSortChange = (columnKey) => {
    const newSortOrder =
      sortColumn === columnKey
        ? sortOrder === "asc"
          ? "desc"
          : "asc"
        : columnKey === "weighted_ai"
          ? "desc"
          : "asc";

    setSortColumn(columnKey);
    setSortOrder(newSortOrder);
  };

  const toggleRowExpansion = (rowKey, task) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(rowKey)) {
        newSet.delete(rowKey);

        analytics.trackPAICCollapseTaskWorkflow({
          from: "pac_role_impact_details",
          email,
          taskIndex: rowKey,
          taskName: task?.parent_task,
          description: `Collapsed task workflow: ${task?.parent_task}`,
        });
      } else {
        newSet.add(rowKey);

        analytics.trackPAICExpandTaskWorkflow({
          from: "pac_role_impact_details",
          email,
          taskIndex: rowKey,
          taskName: task?.parent_task,
          description: `Expanded task workflow: ${task?.parent_task}`,
        });
      }

      return newSet;
    });
  };

  const sortedParentTasks = useMemo(() => {
    if (!roleImpactData?.parent_tasks) return [];

    return [...roleImpactData.parent_tasks].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      if (aValue == null) aValue = "";
      if (bValue == null) bValue = "";

      if (sortColumn === "weighted_ai") {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [roleImpactData?.parent_tasks, sortColumn, sortOrder]);

  const renderSortIcon = (columnKey) => {
    const isActive = sortColumn === columnKey;
    return (
      <SortIcon
        $active={isActive}
        $direction={isActive ? sortOrder : "both"}
        aria-hidden="true"
      />
    );
  };

  const renderComingSoon = (label) => (
    <ComingSoonText>{label} is coming soon.</ComingSoonText>
  );

  // TODO: Dropdown has no functionality - commented out until implemented
  // const peopleAIMenuItems = PEOPLE_PLUS_AI_MENU_ITEMS.map((item) => ({
  //   key: item.key,
  //   label: item.label,
  // }));

  const tabItems = GROUP_DETAIL_TABS.map((tab) => {
    // TODO: Dropdown removed temporarily - no functionality implemented
    // if (tab.key === "people-plus-ai") {
    //   return {
    //     key: tab.key,
    //     label: (
    //       <SbDropdown
    //         menu={{
    //           items: peopleAIMenuItems,
    //           onClick: handlePeopleAIMenuClick,
    //         }}
    //         trigger={["click"]}
    //       >
    //         <TabLabelWithCaret onClick={(e) => e.stopPropagation()}>
    //           {tab.label}
    //           <CaretDownOutlined />
    //         </TabLabelWithCaret>
    //       </SbDropdown>
    //     ),
    //   };
    // }

    return { key: tab.key, label: tab.label };
  });

  const handleSbTabsChange = (tabKey) => {
    if (tabKey === "people-plus-ai") {
      setActiveTab("people-plus-ai");
      return;
    }

    handleTabClick(tabKey);
  };

  if (loading || !roleImpactData) {
    return (
      <PageContainer>
        <LoadingContainer>
          <SbSpin size="large" />
        </LoadingContainer>
      </PageContainer>
    );
  }

  const { job_title, ai_percentage_c, unimpacted_parent_tasks } = roleImpactData;
  const peoplePercent = Math.max(0, 100 - Number(ai_percentage_c || 0));
  const aiPercent = Math.max(0, Number(ai_percentage_c || 0));

  return (
    <PageContainer>
      <HeaderSection>
        <HeaderTopRow>
          <BackButton onClick={handleOnBackClick} type="button">
            <ArrowLeftOutlined />
          </BackButton>
          <Title level={3}>Job Details: {job_title}</Title>
        </HeaderTopRow>

        <TabsAndControlsBlock>
          <TabsRow>
            <SbTabs
              activeKey={activeTab}
              onChange={handleSbTabsChange}
              items={tabItems}
              size="large"
            />
          </TabsRow>
        </TabsAndControlsBlock>
      </HeaderSection>

      <ContentSection>
        {activeTab === "people-plus-ai" && (
          <RoleImpactDetailsContainer>
            <TopSection>
              <SectionTitle>
                People+AI Collaboration Potential for Selected Role
              </SectionTitle>
              <SectionSubTitle>
                A clearer task by task view of where people and AI collaborate
                across the selected role.
              </SectionSubTitle>

              <OverallPotentialCard>
                <OverallPotentialRow>
                  <OverallPotentialLabel>
                    Task break-up
                  </OverallPotentialLabel>

                  <ProgressBarContainer>
                    <ProgressBarCurrent $width={peoplePercent}>
                      {formatPercentage(peoplePercent)} People
                    </ProgressBarCurrent>
                    <ProgressBarAI $width={aiPercent}>
                      {formatPercentage(aiPercent)} AI
                    </ProgressBarAI>
                  </ProgressBarContainer>
                </OverallPotentialRow>
              </OverallPotentialCard>
            </TopSection>

            <TasksTableWrapper>
              <TableElement>
                <colgroup>
                  <TableCol $width="40%" />
                  <TableCol $width="60%" />
                </colgroup>

                <thead>
                  <tr>
                    <TableHeadCell>
                      <SortHeaderButton
                        type="button"
                        onClick={() => handleSortChange("parent_task")}
                      >
                        <SortHeaderLabel>Task</SortHeaderLabel>
                        {renderSortIcon("parent_task")}
                      </SortHeaderButton>
                    </TableHeadCell>

                    <TableHeadCell>
                      <SortHeaderButton
                        type="button"
                        onClick={() => handleSortChange("weighted_ai")}
                      >
                        <SortHeaderLabel>
                          Collaboration Potential
                        </SortHeaderLabel>
                        {renderSortIcon("weighted_ai")}
                      </SortHeaderButton>
                    </TableHeadCell>
                  </tr>
                </thead>

                <tbody>
                  {sortedParentTasks.map((task, index) => {
                    const rowKey = `impacted-${index}-${task?.parent_task || "task"}`;
                    const aiTaskPercentage = Number(task.weighted_ai) || 0;
                    const peoplePercentage = Math.max(0, 100 - aiTaskPercentage);
                    const isExpanded = expandedRows.has(rowKey);

                    return (
                      <React.Fragment key={rowKey}>
                        <tr>
                          <TableBodyCell>
                            <TaskDescription>{task.parent_task}</TaskDescription>
                          </TableBodyCell>

                          <TableBodyCell>
                            <CollaborationPotentialCell>
                              <ProgressBarContainer $compact>
                                <ProgressBarCurrent $width={peoplePercentage}>
                                  {formatPercentage(peoplePercentage)}
                                </ProgressBarCurrent>

                                <ProgressBarAI $width={aiTaskPercentage}>
                                  {formatPercentage(aiTaskPercentage)}
                                </ProgressBarAI>
                              </ProgressBarContainer>

                              <ExpandButton
                                onClick={() => toggleRowExpansion(rowKey, task)}
                                type="button"
                                aria-label={
                                  isExpanded
                                    ? "Collapse workflow details"
                                    : "Expand workflow details"
                                }
                              >
                                <ChevronIcon $expanded={isExpanded} />
                              </ExpandButton>
                            </CollaborationPotentialCell>
                          </TableBodyCell>
                        </tr>

                        {isExpanded && (
                          <ExpandedRow>
                            <ExpandedContent colSpan="2">
                              <ExpandedContentInner>
                                <ExpandedSectionTitle>
                                  How will people and AI collaborate?
                                </ExpandedSectionTitle>

                                <WorkflowCard>
                                  <WorkflowCardHeader>
                                    Task Workflow
                                  </WorkflowCardHeader>

                                  <WorkflowCardContent>
                                    {task.work_flow && task.work_flow.length > 0 ? (
                                      <ol>
                                        {task.work_flow.map((step, stepIndex) => (
                                          <li key={stepIndex}>{step}</li>
                                        ))}
                                      </ol>
                                    ) : (
                                      <EmptyExpandState>
                                        No workflow data available
                                      </EmptyExpandState>
                                    )}
                                  </WorkflowCardContent>
                                </WorkflowCard>
                              </ExpandedContentInner>
                            </ExpandedContent>
                          </ExpandedRow>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {unimpacted_parent_tasks &&
                    unimpacted_parent_tasks.map((task, index) => {
                      const rowKey = `unimpacted-${index}-${task?.parent_task || "task"}`;

                      return (
                        <tr key={rowKey}>
                          <TableBodyCell>
                            <TaskDescription>{task.parent_task}</TaskDescription>
                          </TableBodyCell>

                          <TableBodyCell>
                            <CollaborationPotentialCell>
                              <ProgressBarContainer $compact>
                                <ProgressBarCurrent $width={100}>
                                  100%
                                </ProgressBarCurrent>
                                <ProgressBarAI $width={0} />
                              </ProgressBarContainer>
                              <SpacerCell />
                            </CollaborationPotentialCell>
                          </TableBodyCell>
                        </tr>
                      );
                    })}
                </tbody>
              </TableElement>
            </TasksTableWrapper>
          </RoleImpactDetailsContainer>
        )}

        {activeTab === "learning-catalyst" &&
          renderComingSoon("Learning Catalyst")}
        {activeTab === "talent-mobility" &&
          renderComingSoon("Talent Mobility")}
        {activeTab === "jd" && renderComingSoon("Jobs Library")}
        {activeTab === "skills-bridger" &&
          renderComingSoon("Skills Bridger")}
      </ContentSection>
    </PageContainer>
  );
};

export default SbPACRoleImpactDetailsV2;